<template>
	<div
		class="controls"
		:class="{
			'is-preview': currentUiStatus === UI_STATUS.PREVIEW,
			'is-side-panel': sidePanel,
		}"
	>
		<div
			class="controls__button-row"
			:class="[`is-${currentUiStatus}`]"
		>
			<Button
				v-if="currentUiStatus === UI_STATUS.FINISHED_RECORDING"
				theme="dark"
				modifier="secondary"
				:size="buttonSize"
				:text="$I18n.trans('commons.preview')"
				@click="$emit('previewClip')"
			/>
			<Button
				v-if="currentUiStatus === UI_STATUS.INITIAL"
				:text="initialActionText"
				theme="dark"
				:size="buttonSize"
				@click="handleInitialAction"
			/>
			<Button
				v-if="currentUiStatus === UI_STATUS.RECORDING"
				theme="dark"
				:size="buttonSize"
				:text="$I18n.trans('event.multiformat_editor.labels.stop')"
				@click="$emit('stopClipRecording')"
			/>
			<Button
				v-if="currentUiStatus === UI_STATUS.PREVIEW"
				class="controls__preview-cancel"
				theme="dark"
				modifier="borderless-danger"
				:size="buttonSize"
				:text="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
				@click="$emit('stopPreview')"
			/>
			<Button
				v-if="[UI_STATUS.READY, UI_STATUS.RECORDING].includes(currentUiStatus)"
				theme="dark"
				modifier="borderless-danger"
				:size="buttonSize"
				:text="$Helpers.ucfirst($I18n.trans('commons.cancel'))"
				@click="$emit('cancelReadyStatus')"
			/>
			<Button
				v-if="[UI_STATUS.FINISHED_RECORDING, UI_STATUS.VIDEO_CROP_FAILURE].includes(currentUiStatus)"
				theme="dark"
				modifier="borderless-danger"
				:size="buttonSize"
				:text="$I18n.trans('event.multiformat_editor.labels.restart')"
				@click="$emit('resetClipping')"
			/>
			<template v-if="!hideCreateButton && currentUiStatus === UI_STATUS.FINISHED_RECORDING">
				<div class="controls__flex-grow" />
				<Button
					v-if="!hideCreateButton && currentUiStatus === UI_STATUS.FINISHED_RECORDING"
					:text="$I18n.trans('commons.type_create', {'type': $I18n.trans_choice('commons.video', 1)})"
					theme="dark"
					:size="buttonSize"
					:disabled="createButtonDisabled"
					@click="$emit('createVideoCrop')"
				/>
			</template>
		</div>
		<div
			v-if="!hideGraphicSettings && !isCommunityCropping && [UI_STATUS.FINISHED_RECORDING, UI_STATUS.GENERATING_VIDEO_CROP, UI_STATUS.VIDEO_CROP_AVAILABLE].includes(currentUiStatus)"
			class="controls__graphic-settings"
		>
			<MultiFormatGraphicSelector
				class="controls__graphic-settings-select"
				:value="selectedGraphicId"
				:options="graphicOptions"
				@change="$emit('setSelectedGraphicId', $event)"
			/>
		</div>
		<MultiFormatAspectRatioSelector
			v-if="!hideAspectRatioSelector"
			class="controls__format"
			:value="aspectRatio"
			:options="aspectRatioOptions"
			:disabled="aspectRatioSelectionDisabled"
			@change="$emit('setAspectRatio', $event)"
		/>
		<div
			v-if="!hideInfos"
			class="controls__infos l-button-group"
		>
			<div
				v-if="currentUiStatus === UI_STATUS.RECORDING"
				class="controls__timer"
			>
				<div class="controls__timer-count">
					{{ remainingClipTimeSecsLeadingZero }}
				</div>
				<div class="controls__timer-label">
					{{ $I18n.trans_choice('commons.second', remainingClipTimeSecs) }}
				</div>
			</div>
			<div
				v-if="currentUiStatus === UI_STATUS.PREVIEW"
				class="controls__timer"
			>
				<div class="controls__timer-count">
					{{ previewCountdownLeadingZero }}
				</div>
				<div class="controls__timer-label">
					{{ $I18n.trans_choice('commons.second', previewCountdown) }}
				</div>
			</div>
			<div
				v-if="maxDurationReachedInfoVisible && currentUiStatus === UI_STATUS.FINISHED_RECORDING"
				class="controls__info"
			>
				{{ $I18n.trans('event.multiformat_editor.labels.30s_limit_reached') }}
			</div>
			<!-- STATUS: READY -->
			<div
				v-if="currentUiStatus === UI_STATUS.READY"
				class="controls__info"
			>
				{{ $I18n.trans('event.multiformat_editor.labels.drag_to_start') }}
			</div>
		</div>
	</div>
</template>

<script>
import Button from '@shared/components/Button';
import MultiFormatGraphicSelector from '@shared/components/MultiFormatTool/MultiFormatGraphicSelector.vue';
import MultiFormatAspectRatioSelector from '@shared/components/MultiFormatTool/MultiFormatAspectRatioSelector.vue';
import { GraphicAspectRatio } from '@shared/enums/GraphicAspectRatio.js';

import { UI_STATUS } from './MultiFormatTool';

export default {
	name:       'MultiFormatControls',
	components: { Button, MultiFormatGraphicSelector, MultiFormatAspectRatioSelector },
	props:      {
		currentUiStatus: {
			type:     String,
			required: true,
		},
		selectedGraphicId: {
			type:    [String, Number],
			default: null,
		},
		graphicOptions: {
			type:    Array,
			default: () => [],
		},
		maxDurationReachedInfoVisible: {
			type:    Boolean,
			default: false,
		},
		remainingClipTimeSecs: {
			type:    Number,
			default: 0,
		},
		remainingClipTimeSecsLeadingZero: {
			type:    String,
			default: '',
		},
		previewCountdown: {
			type:    Number,
			default: 0,
		},
		previewCountdownLeadingZero: {
			type:    String,
			default: '',
		},
		isCommunityCropping: {
			type:    Boolean,
			default: false,
		},
		aspectRatio: {
			type:    String,
			default: null,
		},
		aspectRatioOptions: {
			type:    Array,
			default: () => [],
		},
		sidePanel: {
			type:    Boolean,
			default: false,
		},
		hideAspectRatioSelector: {
			type:    Boolean,
			default: false,
		},
		hideGraphicSettings: {
			type:    Boolean,
			default: false,
		},
		hideCreateButton: {
			type:    Boolean,
			default: false,
		},
		hideInfos: {
			type:    Boolean,
			default: false,
		},
		alwaysShowCreateButton: {
			type:    Boolean,
			default: false,
		},
		createButtonDisabled: {
			type:    Boolean,
			default: false,
		},
		buttonSize: {
			type:    String,
			default: 'medium',
		},
	},
	data() {
		return {
			UI_STATUS,
		};
	},
	computed: {
		initialActionText() {
			return this.isDirectRecordingAspectRatio
				? this.$I18n.trans('one.start')
				: this.$I18n.trans('event.multiformat_editor.labels.ready');
		},
		isDirectRecordingAspectRatio() {
			return this.aspectRatio === GraphicAspectRatio.AR_16_9;
		},
		aspectRatioSelectionDisabled() {
			return [
				UI_STATUS.READY,
				UI_STATUS.RECORDING,
				UI_STATUS.PREVIEW,
				UI_STATUS.GENERATING_VIDEO_CROP,
				UI_STATUS.VIDEO_CROP_OVERTIME,
				UI_STATUS.EMAIL_NOTIFICATION_REQUESTED,
			].includes(this.currentUiStatus);
		},
	},
	methods: {
		handleInitialAction() {
			if (this.isDirectRecordingAspectRatio) {
				this.$emit('startClipRecording');
				return;
			}

			this.$emit('setReadyStatus');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.controls {
	width: 100%;
	max-width: 1200px;
	margin: 0 auto;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: $sp5;
	flex-grow: 1;

	&.is-side-panel {
		max-width: none;
		height: 100%;
		margin: 0;
		flex-direction: column;
		align-items: stretch;
		justify-content: flex-start;
		gap: $sp5;
	}

	&.multi-format-tool__workflow-actions {
		width: auto;
		max-width: none;
		margin: 0 0 0 auto;
		flex-grow: 1;
	}

	@include mobile-portrait {
		flex-direction: column;
		align-items: flex-start;

		&.multi-format-tool__workflow-actions {
			flex-direction: row;
			align-items: center;
		}
	}
}

.controls__flex-grow {
	flex-grow: 1;
}

.controls__button-row {
	display: flex;
	gap: $sp3;
	justify-content: flex-start;
	width: auto;
	margin-right: auto;

	.controls.is-side-panel & {
		order: 1;
		width: 100%;
	}

	&.is-finished-recording {
		width: 100%;
	}
}

.controls__infos {
	display: flex;
	gap: $sp4;

	.controls.is-side-panel & {
		order: 3;
		width: 100%;
	}

	@include mobile-portrait {
		width: 100%;
	}
}

.controls__button-group {
	display: flex;
	gap: $sp4;

	> * {
		flex-grow: 1;
		flex-basis: 50%;
	}

	@include mobile-landscape {
		flex-wrap: wrap;
		gap: $sp3;
		padding: 0 $sp2;
	}
}

.controls__info {
	display: flex;
	align-items: center;

	.controls.is-side-panel & {
		min-height: 40px;
		padding: $sp3;
		border-radius: $border-radius--sm;
		background-color: rgba($color-white, 0.04);
	}
}

.controls__timer {
	color: var(--color-grey-300);
	text-align: right;

	.controls.is-side-panel & {
		text-align: left;
	}

	@include mobile-portrait {
		width: 100%;
		text-align: center;
	}
}

.controls__timer-count {
	font-size: 67px; // stylelint-disable-line
	line-height: 1; // stylelint-disable-line
	font-weight: 600; // stylelint-disable-line
}

.controls__timer-label {
	padding-right: 0.2em;
}

// .controls__preview-info-label {}

.controls__preview-info-value {
	@include font-size(fs-150);
}

.controls__graphic-settings {
	.controls.is-side-panel & {
		order: 2;
		width: 100%;
	}

	@include mobile-portrait {
		width: 100%;
	}
}

.controls__format {
	.controls.is-side-panel & {
		order: 1;
		width: 100%;
	}

	@include mobile-portrait {
		width: 100%;
	}
}

.controls__graphic-settings-select {
	background-color: $form-color-selection-active--dark;
	min-width: 240px;
	margin-bottom: 0;

	.controls.is-side-panel & {
		width: 100%;
		min-width: 0;
	}

	@include mobile-portrait {
		background-color: transparent;
	}
}

</style>
